<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useWhatsappEmbeddedSignup } from 'dashboard/composables/useWhatsappEmbeddedSignup';
import api from 'dashboard/api/activationGuide';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import GuideMessage from 'dashboard/components-next/message/ActivationGuideMessage.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

defineProps({ showBackButton: { type: Boolean, default: true } });

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();
const guideAccountId = accountId.value;
let disposed = false;
onBeforeUnmount(() => {
  disposed = true;
});
const { runEmbeddedSignup } = useWhatsappEmbeddedSignup();
const guide = computed(() =>
  store.getters['activationGuide/forAccount'](guideAccountId)
);
const draft = ref('');
const botToken = ref('');
const panel = ref('');
const busy = ref(false);
const sending = ref(false);
const errorKey = ref('');
const requestId = ref(null);
const messagesElement = ref(null);
const currentUser = computed(() => store.getters.getCurrentUser);
const trainingUrl = computed(
  () =>
    `https://wa.me/5561981435045?text=${encodeURIComponent(t('ACTIVATION_GUIDE.TRAINING_MESSAGE'))}`
);
const actions = ['connect_whatsapp', 'connect_email', 'connect_telegram'];
const channelNames = {
  whatsapp: 'WhatsApp',
  google: 'Gmail',
  microsoft: 'Outlook',
  telegram: 'Telegram',
};
const attemptText = computed(() => {
  const attempt = guide.value.attempt;
  if (!attempt) return '';
  const key = attempt.error_category || attempt.status;
  return t(`ACTIVATION_GUIDE.CONNECTION_STATUS.${key}`, {
    channel: channelNames[attempt.channel],
  });
});

const setGuide = data =>
  store.commit('activationGuide/set', { accountId: guideAccountId, data });
const track = action => api.event(guideAccountId, action).catch(() => {});
const refresh = () => store.dispatch('activationGuide/fetch', guideAccountId);
const leave = () =>
  router.replace({ name: 'home', params: { accountId: guideAccountId } });

watch(
  () => guide.value.completed,
  completed => {
    if (!completed) return;
    useAlert(t('ACTIVATION_GUIDE.SUCCESS'));
    leave();
  },
  { immediate: true }
);

watch(
  () => guide.value.loaded && !guide.value.eligible && !guide.value.completed,
  unavailable => {
    if (unavailable) leave();
  }
);

watch(
  () => {
    const latest = guide.value.messages?.at(-1);
    return JSON.stringify([
      latest?.id,
      latest?.status,
      latest?.text,
      panel.value,
    ]);
  },
  async () => {
    await nextTick();
    messagesElement.value?.scrollTo({
      top: messagesElement.value.scrollHeight,
    });
  }
);

onMounted(async () => {
  await refresh();
  if (guide.value.eligible) track('view');
});

const send = async () => {
  if (sending.value || guide.value.pending || !draft.value.trim()) return;
  sending.value = true;
  errorKey.value = '';
  requestId.value ||= crypto.randomUUID();
  try {
    const { data } = await api.send(guideAccountId, {
      guide_text: draft.value,
      request_id: requestId.value,
    });
    setGuide(data);
    draft.value = '';
    requestId.value = null;
  } catch (error) {
    const category = error.response?.data?.error;
    const known = [
      'busy',
      'rate_limit',
      'daily_limit',
      'invalid_input',
      'credentials',
    ];
    errorKey.value = known.includes(category) ? category : 'unavailable';
  } finally {
    sending.value = false;
  }
};

const onComposerKeydown = event => {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  send();
};

const connect = async channel => {
  if (busy.value) return;
  busy.value = true;
  errorKey.value = '';
  let attempt;
  // Start the popup directly from the click, before awaiting our API.
  const signup = channel === 'whatsapp' ? runEmbeddedSignup() : null;
  // Attach a handler immediately while the attempt request is in flight.
  const signupResult = signup?.then(
    credentials => ({ credentials }),
    () => ({ failed: true })
  );
  try {
    ({ data: attempt } = await api.start(guideAccountId, channel));
    if (channel === 'google' || channel === 'microsoft') {
      const { data } = await api.authorization(
        guideAccountId,
        channel,
        attempt.id
      );
      if (!disposed) window.location.assign(data.url);
      return;
    }
    let credentials;
    if (channel === 'whatsapp') {
      const result = await signupResult;
      if (result.failed) throw new Error('authorization');
      credentials = result.credentials;
      if (!credentials) {
        const { data } = await api.updateAttempt(
          guideAccountId,
          attempt.id,
          'cancelled'
        );
        setGuide(data);
        return;
      }
    } else {
      credentials = { bot_token: botToken.value };
      botToken.value = '';
    }
    const { data } = await api.connect(guideAccountId, attempt.id, credentials);
    setGuide(data);
  } catch {
    errorKey.value = 'connection';
    if (attempt)
      await api
        .updateAttempt(guideAccountId, attempt.id, 'failed')
        .catch(() => {});
    await refresh();
  } finally {
    busy.value = false;
  }
};

const performAction = action => {
  track(action);
  if (action === 'connect_whatsapp') connect('whatsapp');
  if (action === 'connect_email') panel.value = 'email';
  if (action === 'connect_telegram') panel.value = 'telegram';
};

const resume = () => {
  const channel = guide.value.attempt.channel;
  const action =
    { whatsapp: 'connect_whatsapp', telegram: 'connect_telegram' }[channel] ||
    'connect_email';
  track(action);
  if (channel === 'telegram') panel.value = 'telegram';
  else connect(channel);
};
</script>

<template>
  <section
    class="conversation-details-wrap relative flex flex-col flex-1 min-w-0 min-h-0 h-full bg-n-surface-1 border-s border-n-weak"
    :aria-label="t('ACTIVATION_GUIDE.IDENTITY')"
  >
    <header
      class="flex shrink-0 items-center gap-2 min-h-14 px-3 py-2 border-b border-n-weak"
    >
      <Button
        v-if="showBackButton"
        icon="i-lucide-arrow-left"
        ghost
        slate
        sm
        :aria-label="t('ACTIVATION_GUIDE.BACK')"
        @click="leave"
      />
      <Avatar
        :name="t('ACTIVATION_GUIDE.NAME')"
        icon-name="i-lucide-bot"
        :size="32"
      />
      <div class="min-w-0">
        <h1 class="text-sm font-medium leading-tight m-0 text-n-slate-12">
          {{ t('ACTIVATION_GUIDE.NAME') }}
        </h1>
        <p class="m-0 text-xs leading-5 text-n-slate-11 truncate">
          {{ t('ACTIVATION_GUIDE.IDENTITY') }}
        </p>
      </div>
    </header>
    <template v-if="guide.eligible">
      <div
        ref="messagesElement"
        role="log"
        aria-live="polite"
        class="conversation-panel flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4"
      >
        <GuideMessage :text="guide.greeting" />
        <div class="flex flex-wrap gap-2 ms-8">
          <Button v-if="guide.attempt" sm :disabled="busy" @click="resume">
            {{ t('ACTIVATION_GUIDE.RESUME') }}
          </Button>
          <Button
            v-for="action in actions"
            :key="action"
            sm
            :color="action === 'connect_whatsapp' ? 'blue' : 'slate'"
            :variant="
              action === 'connect_whatsapp' && !guide.attempt
                ? 'solid'
                : 'outline'
            "
            :disabled="busy"
            @click="performAction(action)"
          >
            {{ t(`ACTIVATION_GUIDE.ACTIONS.${action}`) }}
          </Button>
        </div>
        <div
          v-for="message in guide.messages"
          :key="message.id"
          class="space-y-2"
        >
          <GuideMessage
            v-if="message.text"
            :text="message.text"
            :role="message.role"
            :created-at="message.created_at"
            :sender-name="
              message.role === 'user'
                ? currentUser.name
                : t('ACTIVATION_GUIDE.NAME')
            "
          />
          <div class="flex flex-wrap gap-2 ms-8">
            <Button
              v-for="action in message.actions.filter(item =>
                actions.includes(item)
              )"
              :key="action"
              sm
              outline
              slate
              :disabled="busy"
              @click="performAction(action)"
            >
              {{ t(`ACTIVATION_GUIDE.ACTIONS.${action}`) }}
            </Button>
          </div>
        </div>
        <p
          v-if="guide.pending"
          role="status"
          class="flex items-center gap-2 ms-8 text-sm text-n-slate-11"
        >
          <Spinner class="size-3" />
          {{ t('ACTIVATION_GUIDE.THINKING') }}
        </p>
        <p
          v-if="attemptText"
          role="status"
          class="ms-8 text-sm text-n-slate-11"
        >
          {{ attemptText }}
        </p>
        <div
          v-if="panel"
          class="ms-8 max-w-lg rounded-xl border border-n-weak bg-n-solid-1 p-3 space-y-3"
        >
          <div class="flex items-center justify-between">
            <span class="font-medium">{{
              t(`ACTIVATION_GUIDE.PANELS.${panel}`)
            }}</span>
            <Button
              icon="i-lucide-x"
              ghost
              slate
              sm
              :aria-label="t('ACTIVATION_GUIDE.CLOSE')"
              @click="
                panel = '';
                botToken = '';
              "
            />
          </div>
          <div v-if="panel === 'email'" class="flex flex-wrap gap-2">
            <Button slate :disabled="busy" @click="connect('google')">
              {{ t('ACTIVATION_GUIDE.GMAIL') }}
            </Button>
            <Button slate :disabled="busy" @click="connect('microsoft')">
              {{ t('ACTIVATION_GUIDE.OUTLOOK') }}
            </Button>
            <p class="w-full text-sm m-0">
              {{ t('ACTIVATION_GUIDE.OTHER_EMAIL') }}
            </p>
          </div>
          <form v-else class="space-y-2" @submit.prevent="connect('telegram')">
            <p class="text-sm m-0">
              {{ t('ACTIVATION_GUIDE.TELEGRAM_INSTRUCTIONS') }}
            </p>
            <label for="guide-bot-token" class="text-sm">{{
              t('ACTIVATION_GUIDE.TOKEN')
            }}</label>
            <input
              id="guide-bot-token"
              v-model="botToken"
              type="password"
              autocomplete="off"
              :disabled="busy"
              required
            />
            <Button
              type="submit"
              :disabled="busy || !botToken.trim()"
              :is-loading="busy"
            >
              {{ t('ACTIVATION_GUIDE.CONNECT') }}
            </Button>
          </form>
        </div>
      </div>
      <div class="shrink-0 bg-n-surface-1 px-2 pb-2">
        <p
          v-if="errorKey"
          role="alert"
          class="text-sm text-n-ruby-11 px-3 py-2 m-0"
        >
          {{
            t(`ACTIVATION_GUIDE.ERRORS.${errorKey}`, {
              limit: guide.max_input_characters || 2000,
            })
          }}
        </p>
        <form
          class="relative border border-n-strong rounded-xl bg-n-solid-1 shadow-sm overflow-hidden focus-within:border-n-brand focus-within:ring-1 focus-within:ring-n-brand"
          @submit.prevent="send"
        >
          <div
            class="px-3 py-2 border-b border-n-weak text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.REPLYBOX.REPLY') }}
          </div>
          <label for="guide-message" class="sr-only">{{
            t('ACTIVATION_GUIDE.PLACEHOLDER')
          }}</label>
          <TextArea
            id="guide-message"
            v-model="draft"
            :max-length="guide.max_input_characters || 2000"
            show-character-count
            custom-text-area-class="min-h-16 max-h-40"
            custom-text-area-wrapper-class="!border-0 !rounded-none !bg-transparent"
            :placeholder="t('ACTIVATION_GUIDE.PLACEHOLDER')"
            :disabled="sending"
            @update:model-value="requestId = null"
            @keydown="onComposerKeydown"
          />
          <div class="flex flex-wrap items-center justify-end gap-2 px-3 py-3">
            <a
              :href="trainingUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-8 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 text-sm font-medium text-n-black hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-solid-1"
              @click="track('request_training')"
            >
              <span
                class="i-lucide-graduation-cap size-4 shrink-0"
                aria-hidden="true"
              />
              {{ t('ACTIVATION_GUIDE.ACTIONS.request_training') }}
            </a>
            <Button
              type="submit"
              sm
              icon="i-lucide-send"
              :disabled="sending || guide.pending || !draft.trim()"
              :is-loading="sending"
            >
              {{ t('ACTIVATION_GUIDE.SEND') }}
            </Button>
          </div>
        </form>
      </div>
    </template>
    <p v-else class="p-4">{{ t('ACTIVATION_GUIDE.UNAVAILABLE') }}</p>
    <a
      v-if="!guide.eligible"
      :href="trainingUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="m-2 inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-n-brand px-3 py-2 text-center text-sm font-semibold text-n-black hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 focus-visible:ring-offset-n-solid-1"
      @click="track('request_training')"
    >
      <span
        class="i-lucide-graduation-cap size-5 shrink-0"
        aria-hidden="true"
      />
      {{ t('ACTIVATION_GUIDE.ACTIONS.request_training') }}
    </a>
  </section>
</template>
